import React, { useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { func } from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import { InlineTextButton, PrimaryButton } from '..';
import { updateLandownerInsurancePaymentMethod } from '../../util/api';
import { getPaymentUserMessage } from '../../util/landownerInsuranceEnrollmentErrors';
import { propTypes } from '../../util/types';
import StripePaymentElement from '../StripeCheckoutForm/StripePaymentElement';
import {
  getPaymentMethodUpdateUserMessage,
  getPaymentUpdateBillingDetails,
} from './landownerInsuranceHelpers';
import css from './LandownerInsurance.module.css';

const InsurancePaymentMethodUpdateForm = ({
  currentUser,
  intl,
  onCancel,
  onPaymentMethodUpdated,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handlePaymentMethodUpdate = async () => {
    setIsSubmitting(true);
    setPaymentError(null);
    setPaymentSuccess(false);

    try {
      if (!stripe || !elements) {
        setPaymentError(intl.formatMessage({ id: 'LandownerInsurance.paymentFormLoading' }));
        setIsSubmitting(false);
        return;
      }

      const { error: submitError } = await elements.submit();

      if (submitError) {
        setPaymentError(
          submitError.message || intl.formatMessage({ id: 'LandownerInsurance.updatePaymentError' })
        );
        setIsSubmitting(false);
        return;
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: getPaymentUpdateBillingDetails(currentUser),
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        const userMessage = getPaymentUserMessage(error);
        setPaymentError(
          userMessage || intl.formatMessage({ id: 'LandownerInsurance.updatePaymentError' })
        );
        setIsSubmitting(false);
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;

      if (!paymentMethodId) {
        setPaymentError(intl.formatMessage({ id: 'LandownerInsurance.updatePaymentError' }));
        setIsSubmitting(false);
        return;
      }

      const updateResponse = await updateLandownerInsurancePaymentMethod({
        paymentMethodId,
      });

      if (updateResponse?.clientSecret) {
        if (!stripe) {
          setPaymentError(intl.formatMessage({ id: 'LandownerInsurance.paymentFormLoading' }));
          setIsSubmitting(false);
          return;
        }

        const { error: confirmError } = await stripe.confirmCardPayment(
          updateResponse.clientSecret
        );

        if (confirmError) {
          const userMessage = getPaymentUserMessage(confirmError);
          setPaymentError(
            userMessage || intl.formatMessage({ id: 'LandownerInsurance.updatePaymentError' })
          );
          setIsSubmitting(false);
          return;
        }
      }

      await onPaymentMethodUpdated();
      setPaymentSuccess(true);
    } catch (error) {
      setPaymentError(getPaymentMethodUpdateUserMessage(error, intl));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={css.paymentUpdateForm}>
      <div className={css.sectionIntro}>
        <h3 className={css.sectionTitle}>
          <FormattedMessage id="LandownerInsurance.updatePaymentHeading" />
        </h3>
        <div className={css.supportText}>
          <FormattedMessage id="LandownerInsurance.updatePaymentDescription" />
        </div>
      </div>

      <StripePaymentElement
        paymentElementId="landowner-insurance-update-payment-element"
        showSaveCardCheckbox={false}
        onChange={event => {
          setPaymentError(event?.error?.message || null);
          setPaymentSuccess(false);
        }}
      />

      {paymentError ? <div className={css.errorMessage}>{paymentError}</div> : null}
      {paymentSuccess ? (
        <div className={css.successMessage}>
          <FormattedMessage id="LandownerInsurance.updatePaymentSuccess" />
        </div>
      ) : null}

      <div className={css.paymentUpdateActions}>
        <PrimaryButton
          type="button"
          className={css.updatePaymentButton}
          inProgress={isSubmitting}
          disabled={isSubmitting || !stripe || !elements}
          onClick={handlePaymentMethodUpdate}
        >
          <FormattedMessage id="LandownerInsurance.savePaymentMethod" />
        </PrimaryButton>
        <InlineTextButton
          type="button"
          className={css.cancelPaymentUpdateButton}
          onClick={onCancel}
        >
          <FormattedMessage id="LandownerInsurance.cancelPaymentUpdate" />
        </InlineTextButton>
      </div>
    </div>
  );
};

InsurancePaymentMethodUpdateForm.propTypes = {
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
  onCancel: func.isRequired,
  onPaymentMethodUpdated: func.isRequired,
};

InsurancePaymentMethodUpdateForm.defaultProps = {
  currentUser: null,
};

export default InsurancePaymentMethodUpdateForm;
